import { NavLink, Outlet } from "react-router-dom";
import { Wordmark } from "./Fox.js";
import { common } from "../copy/common.js";
import { useSession } from "../session.js";

/**
 * Two navigations, one source of truth.
 * Phone and portrait tablet get a thumb-reachable bar at the bottom (docs/UX.md §1).
 * From lg up that bar would be a 1200 px wide strip of nothing, so it becomes a
 * side rail and the content is allowed to use the width.
 */
const ITEMS = [
  { to: "/", label: common.nav.start, end: true, icon: HomeIcon },
  { to: "/lists/new", label: common.nav.newList, end: false, icon: PlusIcon },
  { to: "/me", label: common.nav.me, end: false, icon: PersonIcon },
];

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6" aria-hidden>
      <path
        d="M4 11 12 4l8 7v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6" aria-hidden>
      <rect x="3.5" y="4.5" width="17" height="15" rx="3" stroke="currentColor" strokeWidth="2" />
      <path d="M12 9v6M9 12h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6" aria-hidden>
      <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="2" />
      <path
        d="M5 20a7 7 0 0 1 14 0"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

const barItem = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-14 min-w-16 flex-1 flex-col items-center justify-center gap-0.5 rounded-2xl px-2 text-xs font-semibold ${
    isActive ? "bg-fox/10 text-fox-ink" : "text-ink-soft"
  }`;

const railItem = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-12 items-center gap-3 rounded-2xl px-4 py-2.5 font-semibold ${
    isActive ? "bg-fox/12 text-fox-ink" : "text-ink-soft hover:bg-paper-2 hover:text-ink"
  }`;

export function Shell() {
  const { offline } = useSession();
  return (
    <div className="min-h-dvh lg:flex">
      <a href="#inhalt" className="skip-link">
        {common.nav.skipToContent}
      </a>

      <aside className="hidden shrink-0 border-r border-line/70 lg:block lg:w-64 xl:w-72">
        <div className="sticky top-0 flex h-dvh flex-col gap-8 px-5 py-7">
          <NavLink to="/" className="px-2" aria-label={common.nav.wordmarkAria}>
            <Wordmark />
          </NavLink>
          <nav className="flex flex-col gap-1" aria-label={common.nav.mainMenu}>
            {ITEMS.map(({ to, label, end, icon: Icon }) => (
              <NavLink key={to} to={to} end={end} className={railItem}>
                <Icon />
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
      </aside>

      <main
        id="inhalt"
        className="mx-auto w-full max-w-2xl px-4 pt-6 pb-28 sm:px-6 sm:pt-8 lg:mx-0 lg:max-w-none lg:flex-1 lg:px-10 lg:pt-10 lg:pb-16 xl:px-14"
      >
        <div className="mx-auto w-full lg:max-w-4xl xl:max-w-5xl">
          {offline ? (
            // A note, not an error: the app still opens, and nothing is lost.
            <p role="status" className="mb-5 rounded-2xl border border-line/70 bg-paper-2 px-4 py-3 text-ink-soft">
              {common.offline}
            </p>
          ) : null}
          <Outlet />
        </div>
      </main>

      <nav
        className="fixed inset-x-0 bottom-0 z-20 border-t border-line/70 bg-paper/90 backdrop-blur lg:hidden"
        aria-label={common.nav.mainMenu}
      >
        <div className="mx-auto flex max-w-md gap-1 px-3 py-1.5 pb-[max(0.375rem,env(safe-area-inset-bottom))]">
          {ITEMS.map(({ to, label, end, icon: Icon }) => (
            <NavLink key={to} to={to} end={end} className={barItem}>
              <Icon />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
